import { useMemo, useState } from 'react'
import { Dialog } from '@components/Dialog.tsx'
import { useOnline } from '@hooks/useOnline.ts'
import { useResource } from '@hooks/useResource.ts'
import { api } from '@api'
import type { MealDate, MealPlanDay, Recipe } from '@hewey/shared'
import { addDays, dayLabelLine, toIsoDate } from './dates.ts'
import { DayColumn, WeekNavStrip } from './DayColumn.tsx'
import { DaySelectRow } from './DaySelectRow.tsx'
import { useWeekWindow } from './useWeekWindow.ts'
import styles from './AddToPlanDialog.module.css'

type Props = {
	recipe: Recipe
	onClose: () => void
	onAdded: (date: MealDate) => void
}

// Rows size themselves as a fraction of the column, which inside a dialog has
// no flexing parent to measure. 448px is eight 56px rows; the dvh cap leaves
// room for the panel's own chrome — roughly 240px — on a short phone, where a
// row still has to hold a meal name over a note. The panel scrolls if even
// that doesn't fit.
const COLUMN_HEIGHT = 'min(58dvh, 448px)'

// Puts one recipe on one day. The planner's select mode in miniature: the
// same 8-day strip and check affordance, one selection instead of many.
export const AddToPlanDialog = ({ recipe, onClose, onAdded }: Props) => {
	const { weekStart, dates, phase, sliding, slide, onSlideEnd } =
		useWeekWindow()
	const todayIso = useMemo(() => toIsoDate(new Date()), [])

	const online = useOnline()
	const [picked, setPicked] = useState<MealDate | null>(null)
	const [busy, setBusy] = useState(false)
	const [failed, setFailed] = useState(false)

	// Same 3-week window as the planner, so a prev/next slide has its data
	// already. No polling — the dialog is open for seconds, not minutes.
	const fetchFrom = useMemo(
		() => toIsoDate(addDays(weekStart, -7)),
		[weekStart],
	)
	const fetchTo = useMemo(
		() => toIsoDate(addDays(weekStart, 14)),
		[weekStart],
	)
	const resource = useResource(['meal-plan', fetchFrom, fetchTo], () =>
		api.mealPlan.list(fetchFrom, fetchTo),
	)

	const dayMap = useMemo(() => {
		const m = new Map<string, MealPlanDay>()
		for (const d of resource.data?.days ?? []) m.set(d.date, d)
		return m
	}, [resource.data])

	const add = async () => {
		if (!picked) return
		setBusy(true)
		setFailed(false)
		try {
			// The note on the day is human context, not ours to replace —
			// the same rule Suggest follows.
			await api.mealPlan.put(picked, {
				title: recipe.title,
				note: dayMap.get(picked)?.note ?? null,
				recipeId: recipe.id,
			})
			onAdded(picked)
		} catch {
			setFailed(true)
			setBusy(false)
		}
	}

	return (
		<Dialog open onClose={onClose}>
			<form
				className={styles.panel}
				onSubmit={(e) => {
					e.preventDefault()
					if (!busy) void add()
				}}
			>
				<div>
					<h2 className={styles.heading}>Add to meal plan</h2>
					<p className={styles.subheading}>{recipe.title}</p>
				</div>

				<DayColumn
					phase={phase}
					onSlideEnd={onSlideEnd}
					height={COLUMN_HEIGHT}
					top={
						<WeekNavStrip
							dir="prev"
							disabled={sliding}
							onClick={() => slide('prev')}
						/>
					}
					bottom={
						<WeekNavStrip
							dir="next"
							disabled={sliding}
							onClick={() => slide('next')}
						/>
					}
				>
					{dates.map((d) => {
						const iso = toIsoDate(d)
						const day = dayMap.get(iso) ?? null
						return (
							<DaySelectRow
								key={iso}
								date={d}
								day={day}
								isToday={iso === todayIso}
								past={iso < todayIso}
								hasRecipe={(day?.recipeId ?? null) !== null}
								selected={picked === iso}
								pressable={iso >= todayIso}
								proposedTitle={null}
								// One day at a time: tapping another moves the
								// selection rather than adding to it.
								onPress={() => setPicked(iso)}
							/>
						)
					})}
				</DayColumn>

				{failed ? (
					<p className={styles.error}>
						Couldn't add this to the plan. Try again.
					</p>
				) : null}

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.secondary}
						onClick={onClose}
						disabled={busy}
					>
						Cancel
					</button>
					{/* The day is named on the button because navigating a
					    week can carry the picked row off screen. */}
					<button
						type="submit"
						className={styles.primary}
						disabled={busy || picked === null || !online}
					>
						{picked ? `Add to ${dayLabelLine(picked)}` : 'Add'}
					</button>
				</div>
			</form>
		</Dialog>
	)
}
